import { jsPDF } from "jspdf";
import { Context } from "svgcanvas";
import { canv } from "@/canv/global";
import { sleep } from "@/canv/utils";
import type { Group } from "@/group/group";
import "svg2pdf.js";
import "./fonts/Arial-normal";
import "./fonts/Arial-bold";
import "./fonts/Times New Roman-normal";
import "./fonts/Times New Roman-bold";
import { getViewport, sortByIndexByParent } from "@/canvasController/helpers";
import { selectionManager } from "@/tools/cursorTool";
import {
    getDocSize,
    mapGroupsWithoutFrameToFrames,
    prepareAllImagesForExport,
    queryAreaForExport,
    savePDF,
} from "../utils";
import type { EmojiCache } from "./emojiExportUtils";
import { prepareSvgForExport } from "./utils";

export class VectorExport {
    async exportPage(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        canv.setIsHighPdfExporting(true);

        // Получаем фигуры на фреймах
        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);
        const groupsForExport: Group[] = Object.values(groupsByFrames).reduce(
            (acc, cur) => {
                acc.push(...cur);
                return acc;
            },
            [...groupsWithFrame],
        );
        sortByIndexByParent(groupsForExport);
        await this.prepareImagesWithYield(groupsForExport);

        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        canv.zoomToSelected(groupsWithFrame, true);
        const areaWithFrames = canv.canvasController.getSelectedProps(groupsWithFrame);

        const visualWidth: number = areaWithFrames.width;
        const visualHeight: number = areaWithFrames.height;

        const svgCanvas = new Context({
            height: visualHeight,
            width: visualWidth,
            ctx: canv.ctx,
            enableMirroring: false,
        });

        const appCtx = canv.ctx;
        canv.ctx = svgCanvas;

        canv.ctx.setTransform(1, 0, 0, 1, -areaWithFrames.startX, -areaWithFrames.startY);

        await this.renderCanvasWithYield(groupsForExport);

        const svgString = svgCanvas.getSerializedSvg();
        const parser = new DOMParser();
        const svgElement = parser.parseFromString(svgString, "image/svg+xml").documentElement;

        svgElement.setAttribute("viewBox", `0 0 ${visualWidth} ${visualHeight}`);
        svgElement.setAttribute("width", `${visualWidth}px`);
        svgElement.setAttribute("height", `${visualHeight}px`);

        await this.prepareSvgWithYield(svgElement);

        try {
            const doc = new jsPDF();
            await this.drawToDoc(svgElement, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
        }
    }

    async exportPages(groupsWithFrame: Group[], groupsWithoutFrame: Group[]) {
        canv.setIsHighPdfExporting(true);
        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        const groupsByFrames = mapGroupsWithoutFrameToFrames(groupsWithFrame, groupsWithoutFrame);

        const doc = new jsPDF();
        const appCtx = canv.ctx;
        let isFirstPage = true;

        // Создаем кэш для эмодзи для переиспользования между страницами
        const emojiCache: EmojiCache = new Map();

        try {
            for (const frameGroup of groupsWithFrame) {
                const groupsOnFrame = groupsByFrames[frameGroup.UID] || [];
                const groupsForExport = [frameGroup].concat(groupsOnFrame);
                sortByIndexByParent(groupsForExport);
                await this.prepareImagesWithYield(groupsForExport);

                canv.zoomToSelected([frameGroup], true);

                const svgCanvas = new Context({
                    height: frameGroup.height,
                    width: frameGroup.width,
                    ctx: canv.ctx,
                    enableMirroring: false,
                });

                canv.ctx = svgCanvas;
                canv.ctx.setTransform(1, 0, 0, 1, -frameGroup.startX, -frameGroup.startY);

                await this.renderCanvasWithYield(groupsForExport);

                const svgString = svgCanvas.getSerializedSvg();
                const svgElement = new DOMParser().parseFromString(
                    svgString,
                    "image/svg+xml",
                ).documentElement;

                svgElement.setAttribute("viewBox", `0 0 ${frameGroup.width} ${frameGroup.height}`);
                svgElement.setAttribute("width", `${frameGroup.width}px`);
                svgElement.setAttribute("height", `${frameGroup.height}px`);

                await this.prepareSvgWithYield(svgElement, emojiCache);

                await this.drawToDoc(
                    svgElement,
                    frameGroup.width,
                    frameGroup.height,
                    doc,
                    isFirstPage,
                );

                isFirstPage = false;
            }

            savePDF(doc);
        } finally {
            emojiCache.clear();
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);
            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
        }
    }

    async exportViewport() {
        canv.setIsHighPdfExporting(true);

        const viewport = getViewport();

        const groupsForExport =
            queryAreaForExport({
                startX: viewport.viewportStartX,
                startY: viewport.viewportStartY,
                width: viewport.viewportWidth,
                height: viewport.viewportHeight,
            }) ?? [];
        sortByIndexByParent(groupsForExport);
        await this.prepareImagesWithYield(groupsForExport);

        const savedTransformation = {
            "0": canv.m[0],
            "3": canv.m[3],
            "4": canv.m[4],
            "5": canv.m[5],
        };

        const selectedGroups = canv.canvasController.getSelected();
        selectionManager.clearSelected();

        const visualWidth: number = viewport.viewportWidth;
        const visualHeight: number = viewport.viewportHeight;

        const svgCanvas = new Context({
            height: visualHeight,
            width: visualWidth,
            ctx: canv.ctx,
            enableMirroring: false,
        });

        const appCtx = canv.ctx;
        canv.ctx = svgCanvas;

        canv.ctx.setTransform(1, 0, 0, 1, -viewport.viewportStartX, -viewport.viewportStartY);

        await this.renderCanvasWithYield(groupsForExport);

        const svgString = svgCanvas.getSerializedSvg();
        const parser = new DOMParser();
        const svgElement = parser.parseFromString(svgString, "image/svg+xml").documentElement;

        svgElement.setAttribute("viewBox", `0 0 ${visualWidth} ${visualHeight}`);
        svgElement.setAttribute("width", `${visualWidth}px`);
        svgElement.setAttribute("height", `${visualHeight}px`);

        await this.prepareSvgWithYield(svgElement);

        try {
            const doc = new jsPDF();
            await this.drawToDoc(svgElement, visualWidth, visualHeight, doc);
            savePDF(doc);
        } finally {
            canv.ctx = appCtx;
            selectionManager.setSelected(selectedGroups);

            canv.m[0] = savedTransformation["0"];
            canv.m[3] = savedTransformation["3"];
            canv.m[4] = savedTransformation["4"];
            canv.m[5] = savedTransformation["5"];
            canv.ctx.setTransform(canv.m[0], 0, 0, canv.m[3], canv.m[4], canv.m[5]);
            canv.setIsHighPdfExporting(false);
        }
    }

    private async drawToDoc(
        svgElement: HTMLElement,
        visualWidth: number,
        visualHeight: number,
        doc: jsPDF,
        isFirstPage = true,
    ) {
        const docSize = getDocSize(doc, visualWidth, visualHeight);

        if (!isFirstPage) doc.addPage();

        await doc.svg(svgElement, docSize);
        await this.yieldToEventLoop();
    }

    /**
     * Освобождает event loop для обработки ping и других событий
     */
    private async yieldToEventLoop(): Promise<void> {
        await sleep(0);
    }

    /**
     * Подготавливает изображения для экспорта и освобождает event loop
     */
    private async prepareImagesWithYield(groups: Group[]): Promise<void> {
        await prepareAllImagesForExport(groups);
        await this.yieldToEventLoop();
    }

    /**
     * Рендерит canvas и освобождает event loop
     */
    private async renderCanvasWithYield(groupsForExport: Group[]): Promise<void> {
        canv.redraw();
        canv.redraw(true, true, groupsForExport);
        await this.yieldToEventLoop();
    }

    /**
     * Подготавливает SVG для экспорта и освобождает event loop
     */
    private async prepareSvgWithYield(
        svgElement: HTMLElement,
        emojiCache?: EmojiCache,
    ): Promise<void> {
        prepareSvgForExport(svgElement, emojiCache);
        await this.yieldToEventLoop();
    }
}
